import { doc, getDoc, onSnapshot, type Unsubscribe } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { Therapist } from "../types/therapist";
import type { ScheduleTherapist } from "../types/schedule";

const FIRESTORE_CACHE_DOC = doc(db, "_cache", "publicTherapists");

export type ScheduleTherapistListItem = ScheduleTherapist;

let inflight: Promise<Therapist[]> | null = null;

function normalizeTherapists(value: unknown): Therapist[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((t): t is Therapist => Boolean(t && typeof t === "object" && (t as Therapist).id))
    .sort((a, b) =>
      (a.nomePublico || "").localeCompare(b.nomePublico || "", "pt-BR", {
        sensitivity: "base",
      }),
    );
}

async function loadFromFirestore(): Promise<Therapist[] | null> {
  try {
    const snap = await getDoc(FIRESTORE_CACHE_DOC);
    if (!snap.exists()) return null;
    // Lista vazia é válida (ninguém na vitrine).
    return normalizeTherapists(snap.data()?.therapists);
  } catch {
    return null;
  }
}

async function loadFromCallable(): Promise<Therapist[] | null> {
  try {
    const fn = httpsCallable<void, { success: boolean; therapists: Therapist[] }>(
      functions,
      "getPublicTherapists",
    );
    const result = await fn();
    if (!result.data?.success) return null;
    return normalizeTherapists(result.data.therapists);
  } catch {
    return null;
  }
}

/**
 * Carrega terapeutas da vitrine pública.
 * Fonte de verdade: Firestore `_cache/publicTherapists` (atualizado na hora
 * ao marcar/desmarcar no painel). Cloud Function como fallback.
 *
 * O JSON estático da Hostinger NÃO é mais usado — ele atrasava o deploy e
 * fazia nomes já desmarcados continuarem na vitrine.
 */
export async function fetchPublicTherapists(_options?: {
  /** @deprecated Ignorado — mantido só por compatibilidade de chamada. */
  onFast?: (therapists: Therapist[]) => void;
}): Promise<Therapist[]> {
  if (inflight) return inflight;

  inflight = (async () => {
    const firestoreList = await loadFromFirestore();
    if (firestoreList !== null) return firestoreList;

    const callableList = await loadFromCallable();
    return callableList ?? [];
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

/**
 * Assina o cache da vitrine em tempo real.
 * Quando o admin marca/desmarca alguém, a página pública atualiza sozinha.
 */
export function subscribePublicTherapists(
  onChange: (therapists: Therapist[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(
    FIRESTORE_CACHE_DOC,
    (snap) => {
      if (!snap.exists()) {
        onChange([]);
        return;
      }
      onChange(normalizeTherapists(snap.data()?.therapists));
    },
    (err) => {
      onError?.(err);
    },
  );
}

export function toScheduleTherapists(therapists: Therapist[]): ScheduleTherapist[] {
  return therapists.map((t) => ({
    id: t.id,
    nomePublico: t.nomePublico,
    title: t.title || "",
    sessionDuration: t.sessionDuration || 60,
    horariosDisponiveis: t.horariosDisponiveis || {},
  }));
}

/** Pré-carrega o cache Firestore (hover no menu). */
export function prefetchPublicTherapists(): void {
  void loadFromFirestore();
}
